// What a message row draws for this package: the «Fijado» marker (ChannelView.postBody), the seen
// checks as the opener of who-saw-it (SeenBySheet), and requested acknowledgements (shared/API.md
// §41.3): «Confirmar» / «Confirmado» on someone else's post, the count on every requested post, the
// who-confirmed list behind the count.
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { data } from '../../../bridge/client';
import { PostAcknowledgements } from '../../../core/postAcknowledgements';
import { PostSeenDetailsCodec, type PostSeenDetails, type SeenUser } from '../../../core/postSeenDetails';
import { RelativeTime } from '../../../core/relativeTime';
import type { AckState } from '../../../data/features/threads';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { Avatar } from '../../Avatar';
import { Dialog, InlineStatus } from '../../kit/Kit';
import { useLive } from '../../hooks';
import { useContext } from 'react';
import { ShellContext } from '../../shell/ShellContext';
import './threads.css';

/** «📌 Fijado» — accent, above the content, on every pinned post (the flag rides on the post). */
export function PinnedMarker() {
  return (
    <div className="pinned-marker" data-pinned-marker="">
      <Icon name="pin" size={11} strokeWidth={2} />
      {t('channel.pinned.marker')}
    </div>
  );
}

/** «Reenviado» (shared/API.md §31) — above a forwarded post's content, beside where «Fijado» sits. */
export function ForwardedMarker() {
  return (
    <div className="pinned-marker forwarded-marker" data-forwarded-marker="">
      <Icon name="forward" size={11} strokeWidth={2} />
      {t('web.message.forwarded')}
    </div>
  );
}

/** "hoy 14:05" / "ayer 09:14" / "6 sep 08:05" — both halves, the clock following the account. */
function seenLabel(at: number): string {
  return RelativeTime.formatWithTime(at, Date.now(), { yesterday: t('channelList.time.yesterday'), today: t('channelList.time.today') });
}

/** The checks become a button on an OWN synced post: one click opens who has seen it. */
export function SeenOpener({ postId, children }: { postId: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="seen-opener" title={t('web.seen.open')} aria-label={t('web.seen.open')} data-seen-opener={postId} onClick={() => setOpen(true)}>
        {children}
      </button>
      {open ? createPortal(<SeenByDialog postId={postId} onClose={() => setOpen(false)} />, document.body) : null}
    </>
  );
}

type SeenState = { kind: 'loading' } | { kind: 'failed' } | { kind: 'loaded'; details: PostSeenDetails };

export function SeenByDialog({ postId, onClose }: { postId: string; onClose(): void }) {
  const [state, setState] = useState<SeenState>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let alive = true;
    setState({ kind: 'loading' });
    void data.call('seenDetails', postId).then((r) => {
      if (!alive) return;
      setState('details' in r ? { kind: 'loaded', details: r.details } : { kind: 'failed' });
    });
    return () => {
      alive = false;
    };
  }, [postId, attempt]);
  return (
    <Dialog title={t('channel.seen.details')} onClose={onClose} width={420}>
      <div className="seen-by" data-seen-dialog="">
        {state.kind === 'loading' ? <InlineStatus kind="loading" text={t('channel.seen.details')} /> : null}
        {state.kind === 'failed' ? <InlineStatus kind="error" text={t('channel.seen.failed')} onRetry={() => setAttempt((n) => n + 1)} /> : null}
        {state.kind === 'loaded' ? (
          <>
            {state.details.seenUsers.length ? <SeenSection title={tf('channel.seen.sectionSeen', state.details.seenUsers.length)} users={state.details.seenUsers} seen /> : null}
            {state.details.unseenUsers.length ? <SeenSection title={tf('channel.seen.sectionPending', state.details.unseenUsers.length)} users={state.details.unseenUsers} seen={false} /> : null}
          </>
        ) : null}
      </div>
    </Dialog>
  );
}

function SeenSection({ title, users, seen }: { title: string; users: SeenUser[]; seen: boolean }) {
  return (
    <section className="seen-section" data-seen-section={seen ? 'seen' : 'pending'}>
      <h3>{title}</h3>
      {users.map((u) => (
        <div key={u.userId} className={`seen-row${seen ? '' : ' pending'}`}>
          <Avatar userId={u.userId} name={PostSeenDetailsCodec.rowName(u)} size={32} />
          <span className="name">{PostSeenDetailsCodec.rowName(u)}</span>
          {seen && u.seenAt > 0 ? <span className="when">{seenLabel(u.seenAt)}</span> : null}
        </div>
      ))}
    </section>
  );
}

/** §41.3 under a post: «Confirmar» / «Confirmado» + the count (opens the list). */
export function AckBar({ postId, state, me, isOwn, now }: { postId: string; state: AckState | undefined; me: string; isOwn: boolean; now: number }) {
  const [listOpen, setListOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const shell = useContext(ShellContext);
  const toast = (m: string) => shell?.toast(m, 'error');
  if (!state || (!state.requested && !state.acks.length)) return null;
  const s = PostAcknowledgements.summary(state.acks, me, isOwn, now);
  const count = PostAcknowledgements.countLabel(s.count);
  const confirm = async () => {
    setBusy(true);
    const ok = await data.call('acknowledgePost', postId).catch(() => false);
    setBusy(false);
    if (!ok) toast(t('web.ack.failed'));
  };
  const undo = async () => {
    setBusy(true);
    const ok = await data.call('unacknowledgePost', postId).catch(() => false);
    setBusy(false);
    if (!ok) toast(t('web.ack.undoFailed'));
  };
  return (
    <div className="ack-bar" data-ack-bar={postId}>
      {s.offersConfirm && state.requested ? (
        <button type="button" className="ack-confirm" disabled={busy} onClick={() => void confirm()} data-ack-action="confirm">
          <Icon name="check" size={14} strokeWidth={2.2} />
          {t('web.ack.confirm')}
        </button>
      ) : null}
      {s.mine ? (
        <button
          type="button"
          className="ack-confirmed"
          disabled={busy || !s.canUndo}
          title={s.canUndo ? t('web.ack.undo') : undefined}
          aria-label={s.canUndo ? t('web.ack.undo') : t('web.ack.confirmed')}
          onClick={() => s.canUndo && void undo()}
          data-ack-action="undo"
        >
          <Icon name="check" size={14} strokeWidth={2.2} />
          {t('web.ack.confirmed')}
        </button>
      ) : null}
      <button type="button" className="ack-count" onClick={() => setListOpen(true)} data-ack-count={s.count}>
        {count.arg != null ? tf(count.key, count.arg) : t(count.key)}
      </button>
      {listOpen ? createPortal(<AckListDialog postId={postId} me={me} onClose={() => setListOpen(false)} />, document.body) : null}
    </div>
  );
}

function AckListDialog({ postId, me, onClose }: { postId: string; me: string; onClose(): void }) {
  // From the store, live: an ack landing while the list is open shows up in it.
  const { value } = useLive(() => data.call('ackList', postId), ['postAcknowledgements', 'users'], [postId]);
  return (
    <Dialog title={t('web.ack.listTitle')} onClose={onClose} width={420}>
      <div className="seen-by" data-ack-dialog="">
        {value && !value.length ? <p className="ack-empty">{t('web.ack.listEmpty')}</p> : null}
        {(value ?? []).map((r) => (
          <div key={r.userId} className="seen-row">
            <Avatar userId={r.userId} name={r.name} size={32} version={r.version} />
            <span className="name">{r.userId === me ? t('web.ack.you') : r.name}</span>
            <span className="when">{seenLabel(r.acknowledgedAt)}</span>
          </div>
        ))}
      </div>
    </Dialog>
  );
}
